import React, { useState } from 'react';
import { X, Edit, Eye, Mail, Calendar, Tag, User, Phone, MapPin, FileText, Loader2, MessageSquare, Activity, StickyNote, BarChart3, TrendingUp, Save, Send, Bell, Zap } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '@store/authStore';
import { api } from '@services/api';
import toast from 'react-hot-toast';

interface CustomFieldValue {
  customFieldId: string;
  name?: string;
  value: string[];
}

interface Contact {
  contactId: string;
  email: string;
  name?: string;
  campaign?: {
    campaignId: string;
    name: string;
  };
  createdOn?: string;
  changedOn?: string;
  origin?: string;
  ipAddress?: string;
  tags?: Array<{ tagId: string; name: string }>;
  customFieldValues?: CustomFieldValue[];
  geolocation?: {
    latitude?: string;
    longitude?: string;
    city?: string;
    region?: string;
    countryCode?: string;
  };
}

interface ContactDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  companyId: string;
  dataSourceId: string;
  contact: Contact | null;
  onEditClick?: () => void;
  startInEditMode?: boolean; // Start modal in edit mode
}

export const ContactDetailModal: React.FC<ContactDetailModalProps> = ({
  isOpen,
  onClose,
  companyId,
  dataSourceId,
  contact,
  onEditClick,
  startInEditMode = false,
}) => {
  const { accessToken } = useAuthStore();
  
  // Tab state
  const [activeTab, setActiveTab] = useState<'overview' | 'custom-fields' | 'tags' | 'messages' | 'push-notifications' | 'events' | 'activity' | 'insights' | 'notes'>('overview');
  
  // Edit mode state
  const [isEditMode, setIsEditMode] = useState(startInEditMode);
  
  // Reset edit mode when modal opens/closes or startInEditMode changes
  React.useEffect(() => {
    if (isOpen) {
      setIsEditMode(startInEditMode);
    }
  }, [isOpen, startInEditMode]);
  
  // Editable field states
  const [editName, setEditName] = useState('');
  const [editEmail, setEditEmail] = useState('');
  const [editPhone, setEditPhone] = useState('');
  const [editOrigin, setEditOrigin] = useState('');
  const [editCampaignId, setEditCampaignId] = useState('');
  const [editCustomFields, setEditCustomFields] = useState<Map<string, string[]>>(new Map());

  // Fetch full contact details with ALL custom fields from GetResponse API
  const { data: fullContactData, isLoading: isLoadingContact, error: contactError } = useQuery({
    queryKey: ['getresponse-contact-full', companyId, dataSourceId, contact?.contactId],
    queryFn: async () => {
      if (!contact?.contactId) return null;
      
      console.log('🔍 [DetailModal] Fetching contact:', {
        contactId: contact.contactId,
        email: contact.email,
        companyId,
        dataSourceId,
      });
      
      const response = await api.get(`/api/v1/getresponse/contacts/${contact.contactId}`, {
        params: { companyId, dataSourceId },
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      
      console.log('✅ [DetailModal] Full contact from API:', response.data.data);
      console.log('📋 [DetailModal] Custom fields count:', response.data.data?.customFieldValues?.length || 0);
      
      if (response.data.data?.customFieldValues) {
        console.log('🔍 [DetailModal] Custom fields:', response.data.data.customFieldValues);
      }
      
      return response.data.data;
    },
    enabled: isOpen && !!companyId && !!dataSourceId && !!contact?.contactId,
    staleTime: 0, // Always fetch fresh data
    retry: 2, // Retry failed requests
  });

  // Fetch custom fields definitions to map IDs to names
  const { data: customFieldsData } = useQuery({
    queryKey: ['getresponse-custom-fields', companyId, dataSourceId],
    queryFn: async () => {
      const response = await api.get('/api/v1/getresponse/custom-fields', {
        params: { companyId, dataSourceId },
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      return response.data.data;
    },
    enabled: isOpen && !!companyId && !!dataSourceId,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });

  // Debug: Check if query should be enabled
  console.log('🔍 [DetailModal] Lists Query Status:', {
    isOpen,
    companyId,
    dataSourceId,
    enabled: isOpen && !!companyId && !!dataSourceId,
  });

  // Fetch available lists for the dropdown
  const { data: listsData, isLoading: isLoadingLists, error: listsError, refetch: refetchLists } = useQuery({
    queryKey: ['getresponse-lists-modal', companyId, dataSourceId], // Different key from main page
    queryFn: async () => {
      console.log('🔄 [DetailModal] FETCHING LISTS...', { companyId, dataSourceId });
      const response = await api.get('/api/v1/getresponse/lists', {
        params: { companyId, dataSourceId },
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      console.log('📋 [DetailModal] Lists RAW response:', JSON.stringify(response.data, null, 2));
      // Handle different response formats
      let lists = [];
      if (Array.isArray(response.data)) {
        lists = response.data;
      } else if (response.data.data?.lists) {
        lists = response.data.data.lists; // MongoDB format: { data: { lists: [...] } }
      } else if (response.data.data) {
        lists = Array.isArray(response.data.data) ? response.data.data : [];
      } else if (response.data.lists) {
        lists = response.data.lists;
      }
      console.log('✅ [DetailModal] Parsed lists count:', lists.length);
      console.log('✅ [DetailModal] Parsed lists:', lists);
      return lists;
    },
    enabled: isOpen && !!companyId && !!dataSourceId,
    staleTime: 0, // Always fetch fresh data
    refetchOnMount: 'always', // Force refetch when modal opens
  });

  console.log('📊 [DetailModal] Lists Data State:', {
    listsData,
    isLoadingLists,
    listsError,
    isArray: Array.isArray(listsData),
    length: Array.isArray(listsData) ? listsData.length : 'N/A',
  });
  
  // Initialize edit fields when contact data loads
  React.useEffect(() => {
    if (fullContactData && contact) {
      setEditName(fullContactData.name || contact.name || '');
      setEditEmail(fullContactData.email || contact.email || '');
      setEditOrigin(fullContactData.origin || '');
      setEditCampaignId(fullContactData.campaign?.campaignId || contact.campaign?.campaignId || '');
      
      // Initialize phone from custom fields
      const phoneField = fullContactData.customFieldValues?.find((cf: any) => 
        cf.name?.toLowerCase() === 'phone' || cf.name?.toLowerCase() === 'phone_number'
      );
      setEditPhone(phoneField?.value?.[0] || '');
      
      // Initialize all custom fields for editing
      const fieldsMap = new Map<string, string[]>();
      fullContactData.customFieldValues?.forEach((cf: any) => {
        fieldsMap.set(cf.customFieldId, cf.value || []);
      });
      setEditCustomFields(fieldsMap);
    }
  }, [fullContactData, contact]);

  // Handle save changes
  const handleSaveChanges = async () => {
    try {
      const updateData: any = {
        companyId,
        dataSourceId,
        name: editName,
      };

      // Add email if changed
      if (editEmail && editEmail !== (fullContactData?.email || contact.email)) {
        updateData.email = editEmail;
      }

      // Add phone if changed (backend expects 'phone' directly)
      if (editPhone) {
        updateData.phone = editPhone;
      }

      // Add campaign/list if changed
      if (editCampaignId) {
        updateData.campaignId = editCampaignId;
      }

      // Add all custom field changes
      const customFieldsArray: any[] = [];
      editCustomFields.forEach((value, customFieldId) => {
        // Only include fields that have values
        if (value && value.length > 0 && value[0] !== '') {
          customFieldsArray.push({
            customFieldId,
            value,
          });
        }
      });

      if (customFieldsArray.length > 0) {
        updateData.customFields = customFieldsArray;
      }

      console.log('💾 Saving contact changes:', updateData);

      // Update via backend
      const response = await api.put(
        `/api/v1/getresponse/contacts/${contact.contactId}`,
        updateData,
        {
          headers: { Authorization: `Bearer ${accessToken}` },
        }
      );

      console.log('✅ Contact updated:', response.data);
      toast.success('Contact updated successfully!');
      setIsEditMode(false);
      
      // Refetch contact data
      refetch();
    } catch (error: any) {
      console.error('❌ Error updating contact:', error);
      toast.error(error.response?.data?.error || 'Failed to update contact');
    }
  };

  // Handle cancel edit
  const handleCancelEdit = () => {
    setIsEditMode(false);
    // Reset to original values
    if (fullContactData && contact) {
      setEditName(fullContactData.name || contact.name || '');
      setEditEmail(fullContactData.email || contact.email || '');
      setEditOrigin(fullContactData.origin || '');
      setEditCampaignId(fullContactData.campaign?.campaignId || contact.campaign?.campaignId || '');
      
      const phoneField = fullContactData.customFieldValues?.find((cf: any) => 
        cf.name?.toLowerCase() === 'phone' || cf.name?.toLowerCase() === 'phone_number'
      );
      setEditPhone(phoneField?.value?.[0] || '');
      
      // Reset custom fields
      const fieldsMap = new Map<string, string[]>();
      fullContactData.customFieldValues?.forEach((cf: any) => {
        fieldsMap.set(cf.customFieldId, cf.value || []);
      });
      setEditCustomFields(fieldsMap);
    }
  };

  if (!isOpen || !contact) return null;

  // Create a map of customFieldId -> field definition
  const customFieldsMap = new Map(
    customFieldsData?.map((field: any) => [field.customFieldId, field]) || []
  );

  // Get all custom field values with their names
  const customFieldValues = (fullContactData?.customFieldValues || []).map((cf: any) => {
    const fieldDef = customFieldsMap.get(cf.customFieldId);
    return {
      ...cf,
      name: fieldDef?.name || cf.customFieldId,
      type: fieldDef?.type || 'text',
    };
  });

  // Format date for display
  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return dateString;
    }
  };

  // Don't render if modal is closed or contact is null
  if (!isOpen || !contact) {
    return null;
  }

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-7xl w-full max-h-[95vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white dark:bg-gray-800 z-10 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between p-6 pb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center">
                <Eye className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                  {fullContactData?.name || contact.name || 'Contact Details'}
                </h2>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {contact.email}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {isEditMode ? (
                <>
                  <button
                    onClick={handleSaveChanges}
                    className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors flex items-center gap-2"
                  >
                    <Save className="w-4 h-4" />
                    Save Changes
                  </button>
                  <button
                    onClick={handleCancelEdit}
                    className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors"
                  >
                    Cancel
                  </button>
                </>
              ) : (
                <button
                  onClick={() => setIsEditMode(true)}
                  className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors flex items-center gap-2"
                >
                  <Edit className="w-4 h-4" />
                  Edit
                </button>
              )}
              <button
                onClick={onClose}
                disabled={isEditMode}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
          
          {/* Tabs */}
          <div className="flex gap-1 px-6">
            <button
              onClick={() => setActiveTab('overview')}
              className={`px-4 py-2 font-medium text-sm transition-colors border-b-2 ${
                activeTab === 'overview'
                  ? 'border-blue-600 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
              }`}
            >
              <User className="w-4 h-4 inline mr-2" />
              Overview
            </button>
            <button
              onClick={() => setActiveTab('custom-fields')}
              className={`px-4 py-2 font-medium text-sm transition-colors border-b-2 ${
                activeTab === 'custom-fields'
                  ? 'border-blue-600 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
              }`}
            >
              <FileText className="w-4 h-4 inline mr-2" />
              Custom Fields ({customFieldValues.length})
            </button>
            <button
              onClick={() => setActiveTab('tags')}
              className={`px-4 py-2 font-medium text-sm transition-colors border-b-2 ${
                activeTab === 'tags'
                  ? 'border-blue-600 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
              }`}
            >
              <Tag className="w-4 h-4 inline mr-2" />
              Tags ({(fullContactData?.tags || contact.tags || []).length})
            </button>
            <button
              onClick={() => setActiveTab('messages')}
              className={`px-4 py-2 font-medium text-sm transition-colors border-b-2 ${
                activeTab === 'messages'
                  ? 'border-blue-600 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
              }`}
            >
              <Send className="w-4 h-4 inline mr-2" />
              Messages
            </button>
            <button
              onClick={() => setActiveTab('push-notifications')}
              className={`px-4 py-2 font-medium text-sm transition-colors border-b-2 ${
                activeTab === 'push-notifications'
                  ? 'border-blue-600 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
              }`}
            >
              <Bell className="w-4 h-4 inline mr-2" />
              Push
            </button>
            <button
              onClick={() => setActiveTab('events')}
              className={`px-4 py-2 font-medium text-sm transition-colors border-b-2 ${
                activeTab === 'events'
                  ? 'border-blue-600 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
              }`}
            >
              <Zap className="w-4 h-4 inline mr-2" />
              Events
            </button>
            <button
              onClick={() => setActiveTab('activity')}
              className={`px-4 py-2 font-medium text-sm transition-colors border-b-2 ${
                activeTab === 'activity'
                  ? 'border-blue-600 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
              }`}
            >
              <Activity className="w-4 h-4 inline mr-2" />
              Activity
            </button>
            <button
              onClick={() => setActiveTab('insights')}
              className={`px-4 py-2 font-medium text-sm transition-colors border-b-2 ${
                activeTab === 'insights'
                  ? 'border-blue-600 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
              }`}
            >
              <BarChart3 className="w-4 h-4 inline mr-2" />
              Insights
            </button>
            <button
              onClick={() => setActiveTab('notes')}
              className={`px-4 py-2 font-medium text-sm transition-colors border-b-2 ${
                activeTab === 'notes'
                  ? 'border-blue-600 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
              }`}
            >
              <StickyNote className="w-4 h-4 inline mr-2" />
              Notes
            </button>
          </div>
        </div>

        {/* Tab Content */}
        <div className="p-6">
          {contactError ? (
            <div className="flex flex-col items-center justify-center h-64 space-y-4">
              <div className="w-16 h-16 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center">
                <X className="w-8 h-8 text-red-600 dark:text-red-400" />
              </div>
              <div className="text-center">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                  Failed to load contact details
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {(contactError as any)?.message || 'Unknown error occurred'}
                </p>
                <button
                  onClick={() => window.location.reload()}
                  className="mt-4 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                >
                  Refresh Page
                </button>
              </div>
            </div>
          ) : isLoadingContact ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
            </div>
          ) : (
            <>
              {/* OVERVIEW TAB */}
              {activeTab === 'overview' && (
              <div className="space-y-6">
              {/* Contact Information Section */}
              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                  <User className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                  Contact Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Name
                    </label>
                    {isEditMode ? (
                      <input
                        type="text"
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        className="mt-1 w-full px-3 py-2 text-sm font-medium text-gray-900 dark:text-white bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Enter name"
                      />
                    ) : (
                      <p className="mt-1 text-sm font-medium text-gray-900 dark:text-white">
                        {fullContactData?.name || contact.name || 'Not set'}
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Email
                    </label>
                    {isEditMode ? (
                      <input
                        type="email"
                        value={editEmail}
                        onChange={(e) => setEditEmail(e.target.value)}
                        className="mt-1 w-full px-3 py-2 text-sm font-medium text-gray-900 dark:text-white bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Enter email"
                      />
                    ) : (
                      <p className="mt-1 text-sm font-medium text-gray-900 dark:text-white flex items-center gap-2">
                        <Mail className="w-4 h-4 text-gray-400" />
                        {fullContactData?.email || contact.email}
                      </p>
                    )}
                  </div>
                  
                  {/* Phone - Extract from custom fields */}
                  {(() => {
                    const phoneField = customFieldValues.find((cf: any) => 
                      cf.name?.toLowerCase() === 'phone' || cf.name?.toLowerCase() === 'phone_number'
                    );
                    const phoneValue = phoneField?.value?.[0];
                    if (phoneValue || isEditMode) {
                      return (
                        <div>
                          <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                            Phone
                          </label>
                          {isEditMode ? (
                            <input
                              type="tel"
                              value={editPhone || phoneValue || ''}
                              onChange={(e) => setEditPhone(e.target.value)}
                              className="mt-1 w-full px-3 py-2 text-sm font-medium text-gray-900 dark:text-white bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                              placeholder="+44..."
                            />
                          ) : (
                            <p className="mt-1 text-sm font-medium text-gray-900 dark:text-white flex items-center gap-2">
                              <Phone className="w-4 h-4 text-gray-400" />
                              {phoneValue}
                            </p>
                          )}
                        </div>
                      );
                    }
                    return null;
                  })()}
                  
                  {/* Company - Extract from custom fields */}
                  {(() => {
                    const companyField = customFieldValues.find((cf: any) => 
                      cf.name?.toLowerCase() === 'company' || cf.name?.toLowerCase() === 'company_name'
                    );
                    if (companyField && companyField.value?.[0]) {
                      return (
                        <div>
                          <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                            Company
                          </label>
                          <p className="mt-1 text-sm font-medium text-gray-900 dark:text-white">
                            {companyField.value[0]}
                          </p>
                        </div>
                      );
                    }
                    return null;
                  })()}
                  
                  {/* Country - Extract from custom fields or geolocation */}
                  {(() => {
                    const countryField = customFieldValues.find((cf: any) => 
                      cf.name?.toLowerCase() === 'country' || cf.name?.toLowerCase() === 'country_code'
                    );
                    const countryValue = countryField?.value?.[0] || fullContactData?.geolocation?.countryCode;
                    if (countryValue) {
                      return (
                        <div>
                          <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                            Country
                          </label>
                          <p className="mt-1 text-sm font-medium text-gray-900 dark:text-white flex items-center gap-2">
                            <MapPin className="w-4 h-4 text-gray-400" />
                            {countryValue}
                          </p>
                        </div>
                      );
                    }
                    return null;
                  })()}
                  
                  <div>
                    <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      List
                    </label>
                    {isEditMode ? (
                      <select
                        value={editCampaignId}
                        onChange={(e) => setEditCampaignId(e.target.value)}
                        className="mt-1 w-full px-3 py-2 text-sm font-medium text-gray-900 dark:text-white bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        disabled={isLoadingLists}
                      >
                        <option value="">
                          {isLoadingLists ? 'Loading lists...' : listsError ? 'Error loading lists' : 'Select list...'}
                        </option>
                        {Array.isArray(listsData) && listsData.length > 0 ? (
                          listsData.map((list: any) => (
                            <option key={list.campaignId || list.listId} value={list.campaignId || list.listId}>
                              {list.name}
                            </option>
                          ))
                        ) : !isLoadingLists && (
                          <option value="" disabled>No lists available</option>
                        )}
                      </select>
                    ) : (
                      <p className="mt-1 text-sm font-medium text-gray-900 dark:text-white">
                        {fullContactData?.campaign?.name || contact.campaign?.name || 'N/A'}
                      </p>
                    )}
                  </div>
                  
                  {/* Score - Extract from custom fields or scoring */}
                  {(() => {
                    const scoreField = customFieldValues.find((cf: any) => 
                      cf.name?.toLowerCase() === 'score' || cf.name?.toLowerCase() === 'engagement_score'
                    );
                    const scoreValue = scoreField?.value?.[0] || fullContactData?.engagementScore || fullContactData?.scoring;
                    if (scoreValue) {
                      return (
                        <div>
                          <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                            Score
                          </label>
                          <p className="mt-1 text-sm font-medium text-gray-900 dark:text-white">
                            {scoreValue}
                          </p>
                        </div>
                      );
                    }
                    return null;
                  })()}
                  
                  <div>
                    <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Origin
                    </label>
                    {isEditMode ? (
                      <select
                        value={editOrigin}
                        onChange={(e) => setEditOrigin(e.target.value)}
                        className="mt-1 w-full px-3 py-2 text-sm font-medium text-gray-900 dark:text-white bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value="">Select origin</option>
                        <option value="api">API</option>
                        <option value="import">Import</option>
                        <option value="manual">Manual</option>
                        <option value="form">Form</option>
                        <option value="eshop">E-shop</option>
                      </select>
                    ) : (
                      <p className="mt-1 text-sm font-medium text-gray-900 dark:text-white">
                        {fullContactData?.origin || 'N/A'}
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Created
                    </label>
                    <p className="mt-1 text-sm font-medium text-gray-900 dark:text-white flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-gray-400" />
                      {formatDate(fullContactData?.createdOn || contact.createdOn)}
                    </p>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Last Changed
                    </label>
                    <p className="mt-1 text-sm font-medium text-gray-900 dark:text-white flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-gray-400" />
                      {formatDate(fullContactData?.changedOn)}
                    </p>
                  </div>
                  {fullContactData?.ipAddress && (
                    <div>
                      <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        IP Address
                      </label>
                      <p className="mt-1 text-sm font-medium text-gray-900 dark:text-white">
                        {fullContactData.ipAddress}
                      </p>
                    </div>
                  )}
                  {fullContactData?.geolocation && (
                    <div>
                      <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Location
                      </label>
                      <p className="mt-1 text-sm font-medium text-gray-900 dark:text-white flex items-center gap-2">
                        <MapPin className="w-4 h-4 text-gray-400" />
                        {[
                          fullContactData.geolocation.city,
                          fullContactData.geolocation.region,
                          fullContactData.geolocation.countryCode,
                        ]
                          .filter(Boolean)
                          .join(', ') || 'N/A'}
                      </p>
                    </div>
                  )}
                </div>
              </div>
              </div>
              )}

              {/* TAGS TAB */}
              {activeTab === 'tags' && (
              <div className="space-y-4">
              {/* Tags Section */}
              {(fullContactData?.tags || contact.tags) && (fullContactData?.tags?.length > 0 || contact.tags?.length > 0) && (
                <div className="bg-white dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 rounded-lg p-4">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                    <Tag className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                    Tags
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {(fullContactData?.tags || contact.tags || []).map((tag: any, index: number) => (
                      <span
                        key={tag.tagId || index}
                        className="px-3 py-1 bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 text-sm font-medium rounded-full"
                      >
                        {tag.name || tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              </div>
              )}

              {/* CUSTOM FIELDS TAB */}
              {activeTab === 'custom-fields' && (
              <div className="space-y-4">
              {/* Custom Fields Section - SHOW ALL (except ones already displayed above) */}
              {(() => {
                // Filter out fields already shown in Contact Information section
                const fieldsToExclude = ['phone', 'phone_number', 'company', 'company_name', 'country', 'country_code', 'score', 'engagement_score'];
                const filteredCustomFields = customFieldValues.filter((cf: any) => 
                  !fieldsToExclude.includes(cf.name?.toLowerCase())
                );
                
                if (filteredCustomFields && filteredCustomFields.length > 0) {
                  return (
                    <div className="bg-white dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 rounded-lg p-4">
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                        <FileText className="w-5 h-5 text-green-600 dark:text-green-400" />
                        Custom Fields ({filteredCustomFields.length})
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {filteredCustomFields.map((cf: any, index: number) => {
                          const currentValue = editCustomFields.get(cf.customFieldId)?.[0] || cf.value?.[0] || '';
                          
                          return (
                            <div
                              key={cf.customFieldId || index}
                              className="bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 rounded-lg p-3"
                            >
                              <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider block mb-1">
                                {cf.name}
                              </label>
                              {isEditMode ? (
                                cf.type === 'single_select' ? (
                                  <select
                                    value={currentValue}
                                    onChange={(e) => {
                                      const newMap = new Map(editCustomFields);
                                      newMap.set(cf.customFieldId, [e.target.value]);
                                      setEditCustomFields(newMap);
                                    }}
                                    className="w-full px-2 py-1.5 text-sm font-medium text-gray-900 dark:text-white bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                  >
                                    <option value="">Select...</option>
                                    {/* Common options - can be enhanced with field definitions */}
                                    {cf.name?.toLowerCase().includes('gender') && (
                                      <>
                                        <option value="Male">Male</option>
                                        <option value="Female">Female</option>
                                        <option value="Other">Other</option>
                                      </>
                                    )}
                                    {cf.name?.toLowerCase().includes('status') && (
                                      <>
                                        <option value="Active">Active</option>
                                        <option value="Inactive">Inactive</option>
                                        <option value="Pending">Pending</option>
                                      </>
                                    )}
                                    {cf.name?.toLowerCase().includes('tier') && (
                                      <>
                                        <option value="Bronze">Bronze</option>
                                        <option value="Silver">Silver</option>
                                        <option value="Gold">Gold</option>
                                        <option value="Platinum">Platinum</option>
                                        <option value="VIP">VIP</option>
                                      </>
                                    )}
                                    {cf.name?.toLowerCase().includes('consent') && (
                                      <>
                                        <option value="Yes">Yes</option>
                                        <option value="No">No</option>
                                      </>
                                    )}
                                  </select>
                                ) : cf.type === 'date' ? (
                                  <input
                                    type="date"
                                    value={currentValue}
                                    onChange={(e) => {
                                      const newMap = new Map(editCustomFields);
                                      newMap.set(cf.customFieldId, [e.target.value]);
                                      setEditCustomFields(newMap);
                                    }}
                                    className="w-full px-2 py-1.5 text-sm font-medium text-gray-900 dark:text-white bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                  />
                                ) : cf.type === 'number' ? (
                                  <input
                                    type="number"
                                    value={currentValue}
                                    onChange={(e) => {
                                      const newMap = new Map(editCustomFields);
                                      newMap.set(cf.customFieldId, [e.target.value]);
                                      setEditCustomFields(newMap);
                                    }}
                                    className="w-full px-2 py-1.5 text-sm font-medium text-gray-900 dark:text-white bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                  />
                                ) : (
                                  <input
                                    type="text"
                                    value={currentValue}
                                    onChange={(e) => {
                                      const newMap = new Map(editCustomFields);
                                      newMap.set(cf.customFieldId, [e.target.value]);
                                      setEditCustomFields(newMap);
                                    }}
                                    className="w-full px-2 py-1.5 text-sm font-medium text-gray-900 dark:text-white bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    placeholder={`Enter ${cf.name}`}
                                  />
                                )
                              ) : (
                                <p className="mt-1 text-sm font-medium text-gray-900 dark:text-white break-words">
                                  {cf.value && cf.value.length > 0 ? cf.value.join(', ') : 'Not set'}
                                </p>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                }
                return null;
              })()}

              {/* No custom fields message */}
              {(!customFieldValues || customFieldValues.length === 0) && (
                <div className="bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 rounded-lg p-6 text-center">
                  <FileText className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    No custom fields available for this contact
                  </p>
                </div>
              )}
              </div>
              )}

              {/* MESSAGES TAB */}
              {activeTab === 'messages' && (
              <div className="space-y-4">
                <div className="bg-white dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                    <Send className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                    Email Messages
                  </h3>
                  <div className="space-y-3">
                    {/* Placeholder for message list */}
                    <div className="border-l-4 border-blue-500 bg-blue-50 dark:bg-blue-900/20 p-4 rounded-r-lg">
                      <p className="text-sm font-medium text-gray-900 dark:text-white mb-1">
                        🔄 Syncing from GetResponse API
                      </p>
                      <p className="text-xs text-gray-600 dark:text-gray-400">
                        Email campaigns sent to this contact will appear here
                      </p>
                    </div>
                    <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                      <Mail className="w-12 h-12 mx-auto mb-2 opacity-50" />
                      <p className="text-sm">Message history coming soon</p>
                      <p className="text-xs mt-1">Campaign emails • Opens • Clicks • Replies</p>
                    </div>
                  </div>
                </div>
              </div>
              )}

              {/* PUSH NOTIFICATIONS TAB */}
              {activeTab === 'push-notifications' && (
              <div className="space-y-4">
                <div className="bg-white dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                    <Bell className="w-5 h-5 text-orange-600 dark:text-orange-400" />
                    Push Notifications
                  </h3>
                  <div className="space-y-3">
                    <div className="border-l-4 border-orange-500 bg-orange-50 dark:bg-orange-900/20 p-4 rounded-r-lg">
                      <p className="text-sm font-medium text-gray-900 dark:text-white mb-1">
                        🔄 Syncing from GetResponse API
                      </p>
                      <p className="text-xs text-gray-600 dark:text-gray-400">
                        Push notifications sent to this contact will appear here
                      </p>
                    </div>
                    <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                      <Bell className="w-12 h-12 mx-auto mb-2 opacity-50" />
                      <p className="text-sm">Push notification history coming soon</p>
                      <p className="text-xs mt-1">Sent notifications • Delivery status • Click tracking</p>
                    </div>
                  </div>
                </div>
              </div>
              )}

              {/* EVENTS TAB */}
              {activeTab === 'events' && (
              <div className="space-y-4">
                <div className="bg-white dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                    <Zap className="w-5 h-5 text-yellow-600 dark:text-yellow-400" />
                    Event Tracking
                  </h3>
                  <div className="space-y-3">
                    <div className="border-l-4 border-yellow-500 bg-yellow-50 dark:bg-yellow-900/20 p-4 rounded-r-lg">
                      <p className="text-sm font-medium text-gray-900 dark:text-white mb-1">
                        🔄 Syncing from GetResponse API
                      </p>
                      <p className="text-xs text-gray-600 dark:text-gray-400">
                        Custom events and tracking data will appear here
                      </p>
                    </div>
                    {/* Show some events from custom fields */}
                    {(() => {
                      const lastEventType = customFieldValues.find((cf: any) => 
                        cf.name?.toLowerCase() === 'last_event_type'
                      );
                      const lastEventDate = customFieldValues.find((cf: any) => 
                        cf.name?.toLowerCase() === 'last_event_date'
                      );
                      if (lastEventType?.value?.[0]) {
                        return (
                          <div className="bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                            <div className="flex items-start justify-between">
                              <div className="flex items-start gap-3">
                                <div className="w-2 h-2 bg-green-500 rounded-full mt-2"></div>
                                <div>
                                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                                    {lastEventType.value[0]}
                                  </p>
                                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                    {lastEventDate?.value?.[0] || 'Unknown date'}
                                  </p>
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      }
                      return null;
                    })()}
                    <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                      <Zap className="w-12 h-12 mx-auto mb-2 opacity-50" />
                      <p className="text-sm">Full event timeline coming soon</p>
                      <p className="text-xs mt-1">Page visits • Button clicks • Custom events</p>
                    </div>
                  </div>
                </div>
              </div>
              )}

              {/* ACTIVITY TAB */}
              {activeTab === 'activity' && (
              <div className="space-y-4">
                <div className="bg-white dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                    <MessageSquare className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                    Email Activity
                  </h3>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
                      <div>
                        <p className="text-sm font-medium text-gray-900 dark:text-white">Total Emails Sent</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">From campaigns</p>
                      </div>
                      <span className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                        {fullContactData?.activities?.length || 0}
                      </span>
                    </div>
                    <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                      <Activity className="w-12 h-12 mx-auto mb-2 opacity-50" />
                      <p className="text-sm">Detailed activity tracking coming soon</p>
                      <p className="text-xs mt-1">Email opens, clicks, and engagement metrics</p>
                    </div>
                  </div>
                </div>
              </div>
              )}

              {/* INSIGHTS TAB */}
              {activeTab === 'insights' && (
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {/* Total Orders */}
                  {(() => {
                    const totalOrdersField = customFieldValues.find((cf: any) => 
                      cf.name?.toLowerCase() === 'total_orders'
                    );
                    return (
                      <div className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Orders</p>
                            <p className="text-3xl font-bold text-blue-600 dark:text-blue-400 mt-1">
                              {totalOrdersField?.value?.[0] || 0}
                            </p>
                          </div>
                          <TrendingUp className="w-12 h-12 text-blue-600 dark:text-blue-400 opacity-20" />
                        </div>
                      </div>
                    );
                  })()}
                  
                  {/* Total Spent */}
                  {(() => {
                    const totalSpentField = customFieldValues.find((cf: any) => 
                      cf.name?.toLowerCase() === 'total_spent'
                    );
                    return (
                      <div className="bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 border border-green-200 dark:border-green-700 rounded-lg p-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Spent</p>
                            <p className="text-3xl font-bold text-green-600 dark:text-green-400 mt-1">
                              £{totalSpentField?.value?.[0] || 0}
                            </p>
                          </div>
                          <BarChart3 className="w-12 h-12 text-green-600 dark:text-green-400 opacity-20" />
                        </div>
                      </div>
                    );
                  })()}
                  
                  {/* Customer Tier */}
                  {(() => {
                    const tierField = customFieldValues.find((cf: any) => 
                      cf.name?.toLowerCase() === 'customer_tier'
                    );
                    return (
                      <div className="bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 border border-purple-200 dark:border-purple-700 rounded-lg p-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Customer Tier</p>
                            <p className="text-3xl font-bold text-purple-600 dark:text-purple-400 mt-1">
                              {tierField?.value?.[0] || 'N/A'}
                            </p>
                          </div>
                          <User className="w-12 h-12 text-purple-600 dark:text-purple-400 opacity-20" />
                        </div>
                      </div>
                    );
                  })()}
                  
                  {/* Days Since Last Order */}
                  {(() => {
                    const daysField = customFieldValues.find((cf: any) => 
                      cf.name?.toLowerCase() === 'days_since_last_order'
                    );
                    return (
                      <div className="bg-gradient-to-br from-orange-50 to-red-50 dark:from-orange-900/20 dark:to-red-900/20 border border-orange-200 dark:border-orange-700 rounded-lg p-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Days Since Last Order</p>
                            <p className="text-3xl font-bold text-orange-600 dark:text-orange-400 mt-1">
                              {daysField?.value?.[0] || 'N/A'}
                            </p>
                          </div>
                          <Calendar className="w-12 h-12 text-orange-600 dark:text-orange-400 opacity-20" />
                        </div>
                      </div>
                    );
                  })()}
                  
                  {/* Cart Status */}
                  {(() => {
                    const cartStatusField = customFieldValues.find((cf: any) => 
                      cf.name?.toLowerCase() === 'cart_status'
                    );
                    const cartValueField = customFieldValues.find((cf: any) => 
                      cf.name?.toLowerCase() === 'cart_value'
                    );
                    return (
                      <div className="bg-gradient-to-br from-yellow-50 to-amber-50 dark:from-yellow-900/20 dark:to-amber-900/20 border border-yellow-200 dark:border-yellow-700 rounded-lg p-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Cart Status</p>
                            <p className="text-xl font-bold text-yellow-600 dark:text-yellow-400 mt-1">
                              {cartStatusField?.value?.[0] || 'none'}
                            </p>
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                              Value: £{cartValueField?.value?.[0] || 0}
                            </p>
                          </div>
                          <Activity className="w-12 h-12 text-yellow-600 dark:text-yellow-400 opacity-20" />
                        </div>
                      </div>
                    );
                  })()}
                  
                  {/* Last Event */}
                  {(() => {
                    const lastEventTypeField = customFieldValues.find((cf: any) => 
                      cf.name?.toLowerCase() === 'last_event_type'
                    );
                    const lastEventDateField = customFieldValues.find((cf: any) => 
                      cf.name?.toLowerCase() === 'last_event_date'
                    );
                    return (
                      <div className="bg-gradient-to-br from-cyan-50 to-blue-50 dark:from-cyan-900/20 dark:to-blue-900/20 border border-cyan-200 dark:border-cyan-700 rounded-lg p-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Last Activity</p>
                            <p className="text-lg font-bold text-cyan-600 dark:text-cyan-400 mt-1">
                              {lastEventTypeField?.value?.[0] || 'N/A'}
                            </p>
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                              {lastEventDateField?.value?.[0] || 'Unknown'}
                            </p>
                          </div>
                          <MessageSquare className="w-12 h-12 text-cyan-600 dark:text-cyan-400 opacity-20" />
                        </div>
                      </div>
                    );
                  })()}
                </div>
                
                {/* Visual Charts Placeholder */}
                <div className="bg-white dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Activity Timeline</h3>
                  <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                    <BarChart3 className="w-16 h-16 mx-auto mb-3 opacity-50" />
                    <p className="text-sm font-medium">Advanced Charts Coming Soon</p>
                    <p className="text-xs mt-2">Orders over time • Spending trends • Engagement metrics</p>
                  </div>
                </div>
              </div>
              )}

              {/* NOTES TAB */}
              {activeTab === 'notes' && (
              <div className="space-y-4">
                <div className="bg-white dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                    <StickyNote className="w-5 h-5 text-yellow-600 dark:text-yellow-400" />
                    Notes
                  </h3>
                  <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                    <StickyNote className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">Notes feature coming soon</p>
                    <p className="text-xs mt-1">Add and manage contact notes</p>
                  </div>
                </div>
              </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

